import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogFooter,
  DialogTrigger,
  Flex,
  Heading,
  LoadingOrbit,
  Text,
  TextField,
  useToast,
  TagBadge,
  type TagColor,
} from '@chatsdet/ui';
import { Globe, Plus, Trash, ArrowsClockwise, Copy, Check } from '@phosphor-icons/react';
import {
  useCheckCustomDomain,
  useCustomDomains,
  useDeleteCustomDomain,
  useRegisterCustomDomain,
  type CustomDomain,
  type CustomDomainStatus,
} from '../../../hooks/useCustomDomains';

type CustomDomainsSectionProps = {
  isAdmin: boolean;
};

const STATUS_LABELS: Record<CustomDomainStatus, { label: string; color: TagColor }> = {
  pending_validation: { label: 'Pending DNS validation', color: 'yellow' },
  issued: { label: 'Certificate issued', color: 'blue' },
  attached: { label: 'Active', color: 'green' },
  failed: { label: 'Failed', color: 'red' },
  detached: { label: 'Removed', color: 'gray' },
};

function CopyableValue({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <Flex align="center" gap="xs" className="min-w-0">
      <Text size="xs" className="font-mono text-muted-foreground truncate" title={value}>
        {value}
      </Text>
      <Button
        variant="ghost"
        size="sm"
        aria-label="Copy value"
        onPress={() => {
          void navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
      </Button>
    </Flex>
  );
}

function DnsRecordRow({
  name,
  type,
  value,
}: {
  name: string;
  type: string;
  value: string;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 p-2 border rounded-md bg-muted/30">
      <CopyableValue value={name} />
      <TagBadge name={type} color="gray" hideDot size="sm" />
      <CopyableValue value={value} />
    </div>
  );
}

function DomainCard({
  domain,
  albDnsName,
  isAdmin,
  onDelete,
}: {
  domain: CustomDomain;
  albDnsName: string | null;
  isAdmin: boolean;
  onDelete: (domain: CustomDomain) => void;
}) {
  const checkMutation = useCheckCustomDomain();
  const status = STATUS_LABELS[domain.status];
  const needsDns = domain.status === 'pending_validation' || domain.status === 'issued';

  return (
    <div className="p-4 border rounded-lg space-y-3">
      <Flex align="center" justify="between">
        <Flex align="center" gap="sm">
          <Globe size={16} className="text-muted-foreground" />
          <Text weight="medium" className="font-mono">
            {domain.domain}
          </Text>
          <TagBadge name={status.label} color={status.color} size="sm" />
        </Flex>
        <Flex align="center" gap="xs">
          {/* Check attaches the cert to the ALB on success — admin-only server-side */}
          {isAdmin && domain.status !== 'attached' && (
            <Button
              variant="ghost"
              size="sm"
              onPress={() => checkMutation.mutate(domain.id)}
              isDisabled={checkMutation.isPending}
              iconLeft={<ArrowsClockwise size={14} />}
            >
              {checkMutation.isPending ? 'Checking…' : 'Check status'}
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              aria-label="Remove domain"
              onPress={() => onDelete(domain)}
            >
              <Trash size={14} className="text-destructive" />
            </Button>
          )}
        </Flex>
      </Flex>

      {domain.status === 'failed' && domain.errorMessage && (
        <Text size="sm" className="text-destructive">
          {domain.errorMessage}
        </Text>
      )}

      {needsDns && (
        <Box className="space-y-2">
          <Text size="sm" className="text-muted-foreground">
            Add these DNS records in your zone, then check status. Leave both records in
            place permanently — the certificate auto-renews via the validation record.
          </Text>
          {domain.validationRecords.map((record) => (
            <DnsRecordRow
              key={record.name}
              name={record.name}
              type={record.type}
              value={record.value}
            />
          ))}
          {albDnsName && (
            <DnsRecordRow name={`*.${domain.domain}`} type="CNAME" value={albDnsName} />
          )}
        </Box>
      )}

      {domain.status === 'attached' && (
        <Text size="sm" className="text-muted-foreground">
          Sandboxes can now use URLs like{' '}
          <span className="font-mono">https://&lt;name&gt;.{domain.domain}</span>
        </Text>
      )}
    </div>
  );
}

export function CustomDomainsSection({ isAdmin }: CustomDomainsSectionProps) {
  const { toast } = useToast();
  const domainsQuery = useCustomDomains();
  const registerMutation = useRegisterCustomDomain();
  const deleteMutation = useDeleteCustomDomain();

  const [isAddOpen, setIsAddOpen] = React.useState(false);
  const [newDomain, setNewDomain] = React.useState('');
  const [pendingDelete, setPendingDelete] = React.useState<CustomDomain | null>(null);

  const data = domainsQuery.data?.data;
  const domains = data?.domains ?? [];

  const handleRegister = () => {
    registerMutation.mutate(newDomain.trim().toLowerCase(), {
      onSuccess: () => {
        toast.success('Domain registered — add the DNS records to activate it');
        setIsAddOpen(false);
        setNewDomain('');
      },
      onError: (error: Error) => {
        toast.error(error.message);
      },
    });
  };

  const handleDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: () => {
        toast.success('Domain removed');
        setPendingDelete(null);
      },
      onError: (error: Error) => {
        toast.error(error.message);
        setPendingDelete(null);
      },
    });
  };

  return (
    <Box className="space-y-6">
      <Card padding="lg">
        <CardHeader>
          <Flex align="start" gap="md">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Globe size={24} className="text-primary" weight="bold" />
            </div>
            <div className="flex-1">
              <Heading level={3}>Custom Sandbox Domains</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Serve sandboxes from your own domain (e.g.{' '}
                <span className="font-mono">george.demo.yourcompany.com</span>). One-time
                DNS setup per domain; afterwards any sandbox can use a URL under it.
              </Text>
            </div>
            {isAdmin && (
              <Button variant="primary" onPress={() => setIsAddOpen(true)}>
                <Plus size={16} className="mr-2" />
                Add Domain
              </Button>
            )}
          </Flex>
        </CardHeader>
        <CardContent>
          {domainsQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-8">
              <LoadingOrbit size="md" />
            </Flex>
          ) : domains.length > 0 ? (
            <div className="space-y-3">
              {domains.map((domain) => (
                <DomainCard
                  key={domain.id}
                  domain={domain}
                  albDnsName={data?.albDnsName ?? null}
                  isAdmin={isAdmin}
                  onDelete={setPendingDelete}
                />
              ))}
            </div>
          ) : (
            <Text size="sm" className="text-muted-foreground py-4">
              No custom domains yet.
              {isAdmin ? ' Add one to brand your sandbox URLs.' : ''}
            </Text>
          )}
        </CardContent>
      </Card>

      {/* Register dialog */}
      <DialogTrigger isOpen={isAddOpen} onOpenChange={setIsAddOpen}>
        <span />
        <Dialog
          title="Add Custom Domain"
          description="Register a base domain you own. Sandboxes get URLs like <name>.<domain>."
        >
          <Box className="space-y-4">
            <TextField
              label="Domain"
              value={newDomain}
              onChange={setNewDomain}
              placeholder="demo.yourcompany.com"
              description="A subdomain you control. You'll add two DNS records: a certificate-validation CNAME and a wildcard CNAME pointing at our load balancer."
              autoFocus
            />
            <DialogFooter>
              <Flex gap="sm" justify="end">
                <Button variant="outline" onPress={() => setIsAddOpen(false)} type="button">
                  Cancel
                </Button>
                <Button
                  onPress={handleRegister}
                  isDisabled={!newDomain.trim() || registerMutation.isPending}
                >
                  {registerMutation.isPending ? 'Registering…' : 'Register Domain'}
                </Button>
              </Flex>
            </DialogFooter>
          </Box>
        </Dialog>
      </DialogTrigger>

      {/* Delete confirm dialog (in-app — native confirm() wedges agents) */}
      <DialogTrigger isOpen={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <span />
        <Dialog
          title="Remove Custom Domain"
          description={
            pendingDelete
              ? `Remove ${pendingDelete.domain}? Its certificate will be detached and new sandboxes can no longer use this domain.`
              : ''
          }
        >
          <DialogFooter>
            <Flex gap="sm" justify="end">
              <Button variant="outline" onPress={() => setPendingDelete(null)} type="button">
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={handleDelete}
                isDisabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Removing…' : 'Remove Domain'}
              </Button>
            </Flex>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </Box>
  );
}
